import { useCreateMultiProviderAppointmentMutation } from "@/graphql/mutations/createMultiProviderAppointment.graphql.types";
import { useUpdateInvoiceSendReceiptMutation } from "@/graphql/mutations/updateInvoiceSendReceipt.graphql.types";
import { ServicesForBookingAppointmentQuery } from "@/graphql/queries/appointment/servicesForBookingAppointment.graphql.types";
import { ClientForAppointmentCreationQuery } from "@/graphql/queries/clients.graphql.types";
import { MedspaProvidersQuery } from "@/graphql/queries/medspaProviders.graphql.types";
import { TelehealthType } from "@/types";
import { getTelehealthTypeSchema } from "@/utils/telehealth";
import useMedspaTimezone from "../common/useMedspaTimezone";
import { ConfigurationToUpdate } from "./useApptCustomizedCommunication";

type AvailableService =
  ServicesForBookingAppointmentQuery["medspaServiceMenuItem"][number];

type Provider = MedspaProvidersQuery["medspaByPk"]["userMedspas"][number];

type Client = NonNullable<ClientForAppointmentCreationQuery["clientByPk"]>;

type AppointmentServiceInput = {
  serviceMenuItem: AvailableService;
  provider: Provider;
  startTime: Date;
  endTime: Date;
};

export type CreateMultiProviderAppointmentProps = {
  configurationToUpdate: ConfigurationToUpdate;
  appointmentServices: AppointmentServiceInput[];
  client: Client;
  telehealthType: TelehealthType;
  telehealthVideoLink: string;
  telehealthAdditionalDetails: string;
};

export const useCreateMultiProviderAppointment = ({
  configurationToUpdate,
  appointmentServices,
  client,
  telehealthType,
  telehealthVideoLink,
  telehealthAdditionalDetails,
}: CreateMultiProviderAppointmentProps) => {
  const { zonedTimeToUtcIso } = useMedspaTimezone();
  const [updateInvoiceSendReceipt] = useUpdateInvoiceSendReceiptMutation();
  const [createMultiProviderAppointmentMutation] =
    useCreateMultiProviderAppointmentMutation();

  const handleCreateAppointment = async () => {
    const {
      sendAfterPaymentReceiptSms,
      sendAfterPaymentReceiptMail,
      ...apptCommunicationConfig
    } = configurationToUpdate;

    const createAppointmentResult =
      await createMultiProviderAppointmentMutation({
        variables: {
          services: appointmentServices.map(
            ({ serviceMenuItem, provider, startTime, endTime }) => ({
              serviceMenuItemId: serviceMenuItem.id,
              providerId: provider.id,
              startTime: zonedTimeToUtcIso(startTime),
              endTime: zonedTimeToUtcIso(endTime),
            })
          ),
          clientId: client.id,
          appointmentType: getTelehealthTypeSchema(telehealthType),
          videoLink:
            telehealthType === TelehealthType.PHONE
              ? undefined
              : telehealthVideoLink,
          appointmentDetails: telehealthAdditionalDetails,
          config: apptCommunicationConfig,
        },
        refetchQueries: [
          "MultiProviderAppointmentsAndAvailabilityEventsWithDateRange",
          "AppointmentAndTimeBlocksWithDateRange",
        ],
      });

    const invoiceId =
      createAppointmentResult.data.createMultiProviderAppointment.invoiceId;

    if (
      invoiceId &&
      (sendAfterPaymentReceiptSms != null ||
        sendAfterPaymentReceiptMail != null)
    ) {
      updateInvoiceSendReceipt({
        variables: {
          invoiceId,
          sendAfterPaymentReceiptSms,
          sendAfterPaymentReceiptMail,
        },
      });
    }

    return createAppointmentResult;
  };

  return handleCreateAppointment;
};
